import React, { useState, useEffect } from "react";
import { Form, Input, Button, message, Alert, Select } from "antd";
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  CloudOutlined,
  BankOutlined,
} from "@ant-design/icons";
import { Auth } from "aws-amplify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LeftPanel } from "./SignIn";
import "./AuthPages.css";

const { Password } = Input;

const INDUSTRY_PROFILES = [
  {
    value: "FS",
    label: "Financial Services",
    description: "Banking, insurance, asset management, payments, fintech",
  },
  {
    value: "TECH",
    label: "Technology / SaaS",
    description: "Software, SaaS, cloud services, MSPs, cybersecurity",
  },
  {
    value: "HEALTH",
    label: "Healthcare",
    description: "Hospitals, clinics, care homes, health tech, pharmaceuticals",
  },
  {
    value: "CONST",
    label: "Construction / Manufacturing",
    description:
      "Construction, engineering, manufacturing, utilities, logistics",
  },
  {
    value: "GEN",
    label: "Generic / Internal",
    description: "Any organisation \u2013 internal operational controls only",
  },
];

const Signup: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const fromActivation = searchParams.get("from") === "activation";

  useEffect(() => {
    if (!fromActivation) {
      message.warning(
        "Signup is only available through AWS Marketplace activation.",
      );
      setTimeout(() => navigate("/"), 2000);
    }
  }, [fromActivation, navigate]);

  const handleSignup = async (values: any) => {
    try {
      setLoading(true);
      const {
        email,
        firstName,
        lastName,
        password,
        awsAccountId,
        industryProfile,
      } = values;

      await Auth.signUp({
        username: email,
        password,
        attributes: {
          email,
          given_name: firstName,
          family_name: lastName,
          "custom:awsAccountId": awsAccountId,
          "custom:industryProfile": industryProfile,
          // Optimistic: AWS Marketplace signups default to admin. If the user
          // is actually joining an existing tenant (same AWS Account ID),
          // the PostConfirmation Lambda will downgrade them to 'assessor'.
          // Setting it here guarantees the ID token has a real role even if
          // the post-confirmation trigger fails silently.
          "custom:role": "admin",
        },
        autoSignIn: { enabled: true },
      });

      message.success("Account created! Check your email for a verification code.");
      navigate(`/verify-email?email=${encodeURIComponent(email)}`);
    } catch (error: any) {
      console.error("Signup error:", error);
      let errMsg = "Failed to create account";
      if (error.code === "UsernameExistsException") {
        errMsg = "An account with this email already exists";
      } else if (error.code === "InvalidPasswordException") {
        errMsg = "Password does not meet requirements";
      } else if (error.code === "InvalidParameterException") {
        errMsg = error.message || "Invalid input provided";
      } else if (error.message) {
        errMsg = error.message;
      }
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const particles = Array.from({ length: 6 }, (_, i) => (
    <div key={i} className="auth-particle" />
  ));

  if (!fromActivation) {
    return (
      <div
        className="auth-page"
        style={{ justifyContent: "center", alignItems: "center" }}
      >
        {particles}
        <div className="auth-card" style={{ maxWidth: 440 }}>
          <Alert
            message="Access Denied"
            description="Signup is only available through AWS Marketplace activation. Please subscribe first."
            type="warning"
            showIcon
            style={{ marginBottom: 20 }}
          />
          <Button
            className="auth-submit-btn"
            type="primary"
            block
            onClick={() => navigate("/")}
          >
            Go to Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      {particles}
      <LeftPanel />
      <div className="auth-right-panel">
        <div className="auth-card">
          <div className="auth-card-header">
            <div className="auth-card-title">Create Your Account</div>
            <div className="auth-card-subtitle">
              Get started with your AWS Marketplace subscription
            </div>
          </div>

          <Form form={form} layout="vertical" onFinish={handleSignup}>
            <div className="auth-field">
              <label className="auth-label">AWS Account ID</label>
              <div className="auth-input-wrap">
                <CloudOutlined className="auth-input-icon" />
                <Form.Item
                  name="awsAccountId"
                  rules={[
                    { required: true, message: "AWS Account ID is required" },
                    {
                      pattern: /^\d{12}$/,
                      message: "Must be exactly 12 digits",
                    },
                  ]}
                >
                  <Input placeholder="123456789012" maxLength={12} />
                </Form.Item>
              </div>
            </div>

            <div className="auth-field">
              <label className="auth-label">Industry Profile</label>
              <div className="auth-input-wrap">
                <BankOutlined className="auth-input-icon" />
                <Form.Item
                  name="industryProfile"
                  rules={[
                    { required: true, message: "Please select your industry" },
                  ]}
                >
                  <Select
                    // placeholder="Select your industry"
                    optionLabelProp="label"
                    style={{ width: "100%" }}
                  >
                    {INDUSTRY_PROFILES.map((p) => (
                      <Select.Option
                        key={p.value}
                        value={p.value}
                        label={p.label}
                      >
                        <div>
                          <div style={{ fontWeight: 500 }}>{p.label}</div>
                          <div style={{ fontSize: 12, color: "#888" }}>
                            {p.description}
                          </div>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
            </div>

            <div className="auth-field">
              <label className="auth-label">Email Address</label>
              <div className="auth-input-wrap">
                <MailOutlined className="auth-input-icon" />
                <Form.Item
                  name="email"
                  rules={[
                    { required: true, message: "Email is required" },
                    { type: "email", message: "Enter a valid email" },
                  ]}
                >
                  <Input placeholder="you@company.com" />
                </Form.Item>
              </div>
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <div className="auth-field" style={{ flex: 1 }}>
                <label className="auth-label">First Name</label>
                <div className="auth-input-wrap">
                  <UserOutlined className="auth-input-icon" />
                  <Form.Item
                    name="firstName"
                    rules={[
                      { required: true, message: "Required" },
                      { min: 2, message: "Min 2 chars" },
                    ]}
                    >
                    <Input placeholder="Jane" />
                  </Form.Item>
                </div>
              </div>
              <div className="auth-field" style={{ flex: 1 }}>
                <label className="auth-label">Last Name</label>
                <div className="auth-input-wrap">
                  <UserOutlined className="auth-input-icon" />
                  <Form.Item
                    name="lastName"
                    rules={[
                      { required: true, message: "Required" },
                      { min: 2, message: "Min 2 chars" },
                    ]}
                    >
                    <Input placeholder="Smith" />
                  </Form.Item>
                </div>
              </div>
            </div>

            <div className="auth-field">
              <label className="auth-label">Password</label>
              <div className="auth-input-wrap">
                <LockOutlined className="auth-input-icon" />
                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: "Password is required" },
                    { min: 8, message: "Minimum 8 characters" },
                  ]}
                >
                  <Password placeholder="Min 8 characters" />
                </Form.Item>
              </div>
            </div>

            <div className="auth-field">
              <label className="auth-label">Confirm Password</label>
              <div className="auth-input-wrap">
                <LockOutlined className="auth-input-icon" />
                <Form.Item
                  name="confirmPassword"
                  dependencies={["password"]}
                  rules={[
                    { required: true, message: "Please confirm password" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value)
                          return Promise.resolve();
                        return Promise.reject(
                          new Error("Passwords do not match"),
                        );
                      },
                    }),
                  ]}
                >
                  <Password placeholder="Confirm your password" />
                </Form.Item>
              </div>
            </div>

            <Form.Item>
              <Button
                className="auth-submit-btn"
                type="primary"
                htmlType="submit"
                loading={loading}
                block
              >
                Create Account
              </Button>
            </Form.Item>
          </Form>

          <div className="auth-footer">
            Already have an account?{" "}
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
              }}
            >
              Sign in
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
